import { useState, useMemo, useCallback, useEffect } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { $api } from '../../api/queryClient'
import { apiBaseUrl } from '../../api/client'
import { useDebounce } from '../../hooks/useDebounce'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Textarea } from '../../components/ui/textarea'
import { Badge } from '../../components/ui/badge'
import { Card, CardContent } from '../../components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog'
import { mdiCheck, mdiChevronLeft, mdiChevronRight, mdiClose, mdiLoading, mdiMagnify, mdiMapMarker, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from '../../components/ui/icon'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { PageHeader } from '../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

/** Fetch content with auth token and return a blob URL */
function useAuthenticatedBlobUrl(url: string | null): string | null {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!url) {
      setBlobUrl(null)
      return
    }

    let cancelled = false
    let token: string | null = null
    try {
      const authState = JSON.parse(localStorage.getItem('authState') || '{}')
      token = authState.accessToken || null
    } catch { /* ignore */ }

    fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.blob()
      })
      .then((blob) => {
        if (!cancelled) {
          setBlobUrl(URL.createObjectURL(blob))
        }
      })
      .catch(() => {
        if (!cancelled) setBlobUrl(null)
      })

    return () => {
      cancelled = true
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return null
      })
    }
  }, [url])

  return blobUrl
}

export const Route = createFileRoute('/$org/debug_/annotations')({
  component: AnnotationsPage,
})

const PRIORITY_CONFIG: Record<number, { color: string; label: string }> = {
  1: { color: 'bg-red-500', label: 'Critical' },
  2: { color: 'bg-orange-500', label: 'High' },
  3: { color: 'bg-yellow-500', label: 'Medium' },
  4: { color: 'bg-blue-500', label: 'Low' },
  5: { color: 'bg-muted-foreground', label: 'Lowest' },
}

interface AnnotationCoords {
  type: 'point'
  x: number
  y: number
  page: number
}

interface IssueAnnotation {
  uid: string
  title: string
  description?: string
  priority: number
  status: string
  properties?: {
    annotation?: AnnotationCoords
  }
}

function AnnotationsPage() {
  const { org } = Route.useParams()
  const queryClient = useQueryClient()

  useBreadcrumbs([
    { title: 'Home', path: `/${org}` },
    { title: 'Debug', path: `/${org}/debug` },
    { title: 'Annotations' },
  ])

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedQuery = useDebounce(searchQuery, 200)
  const [selectedDocumentUid, setSelectedDocumentUid] = useState<string | null>(null)

  // Fetch documents (images and PDFs)
  const { data: documentsData, isLoading: docsLoading } = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/documents',
    {
      params: {
        path: { orgCode: org },
        query: { q: debouncedQuery, limit: 20 },
      },
    }
  )

  const documents = useMemo(() => {
    const docs = (documentsData as any)?.data || []
    return docs.filter((d: any) => {
      const mime = d.file_mime_type || d.mime_type || ''
      return mime.startsWith('image/') || mime === 'application/pdf'
    })
  }, [documentsData])

  const selectedDocument = useMemo(
    () => documents.find((d: any) => d.uid === selectedDocumentUid),
    [documents, selectedDocumentUid]
  )

  // Fetch issues linked to selected document
  const { data: issuesData, isLoading: issuesLoading } = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/issues',
    {
      params: {
        path: { orgCode: org },
        query: {
          target_entity_type: 'document',
          target_entity_uid: selectedDocumentUid || '',
          assignee_uid: 'all',
          status: 'open,in_progress,closed,draft',
          limit: 100,
        },
      },
    },
    { enabled: !!selectedDocumentUid }
  )

  const issues: IssueAnnotation[] = useMemo(() => {
    return ((issuesData as any)?.data || []) as IssueAnnotation[]
  }, [issuesData])

  const invalidateIssues = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey as any[]
        if (queryKey.length >= 2 && queryKey[0] === 'get') {
          return queryKey[1] === '/api/v1/organizations/{orgCode}/issues'
        }
        return false
      },
    })
  }, [queryClient])

  const isPdf = (selectedDocument?.file_mime_type || selectedDocument?.mime_type) === 'application/pdf'
  const rawContentUrl = selectedDocumentUid
    ? `${apiBaseUrl}/api/v1/organizations/${org}/documents/${selectedDocumentUid}/content`
    : null
  const blobUrl = useAuthenticatedBlobUrl(rawContentUrl)

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <PageHeader className="mb-6" icon={SECTION_ICONS.documents} title="Document Annotations" />

      {/* Document Selector - hidden when a document is selected */}
      {!selectedDocumentUid ? (
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <AppIcon path={mdiMagnify} size="sm" />
              <Input
                placeholder="Search documents (images & PDFs)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="annotation-doc-search"
              />
            </div>
            {docsLoading && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" />
                Loading documents...
              </div>
            )}
            {documents.length > 0 && (
              <div className="grid gap-2 md:grid-cols-3 lg:grid-cols-4">
                {documents.map((doc: any) => (
                  <button
                    key={doc.uid}
                    onClick={() => setSelectedDocumentUid(doc.uid)}
                    className="flex items-center gap-2 rounded-lg border border-border p-3 text-left transition-colors hover:bg-accent"
                    data-testid={`annotation-doc-${doc.uid}`}
                  >
                    <AppIcon path={mdiMapMarker} size="sm" className="shrink-0" />
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{doc.name}</div>
                      <div className="text-xs text-muted-foreground">{doc.file_mime_type || doc.mime_type}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="mb-4 flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectedDocumentUid(null)}
            data-testid="annotation-back-to-docs"
          >
            <AppIcon path={mdiClose} size="sm" className="mr-1" />
            Back to documents
          </Button>
          <span className="text-sm text-muted-foreground">
            {selectedDocument?.name || selectedDocument?.file_name}
          </span>
        </div>
      )}

      {/* Annotation Viewer */}
      {selectedDocumentUid && (
        <Card>
          <CardContent className="pt-6">
            {(issuesLoading || (rawContentUrl && !blobUrl)) && (
              <div className="flex items-center gap-2 text-muted-foreground mb-4">
                <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" />
                Loading...
              </div>
            )}
            {blobUrl && (isPdf ? (
              <PdfAnnotationViewer
                org={org}
                contentUrl={blobUrl}
                documentUid={selectedDocumentUid}
                issues={issues}
                onInvalidate={invalidateIssues}
              />
            ) : (
              <ImageAnnotationViewer
                org={org}
                contentUrl={blobUrl}
                documentUid={selectedDocumentUid}
                issues={issues}
                onInvalidate={invalidateIssues}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// --- Image Annotation Viewer ---

interface ViewerProps {
  org: string
  contentUrl: string
  documentUid: string
  issues: IssueAnnotation[]
  onInvalidate: () => void
}

function ImageAnnotationViewer({ org, contentUrl, documentUid, issues, onInvalidate }: ViewerProps) {
  const [creating, setCreating] = useState<{ x: number; y: number } | null>(null)
  const [selectedIssue, setSelectedIssue] = useState<IssueAnnotation | null>(null)

  const handleImageClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (selectedIssue) {
        setSelectedIssue(null)
        return
      }
      const rect = e.currentTarget.getBoundingClientRect()
      const x = (e.clientX - rect.left) / rect.width
      const y = (e.clientY - rect.top) / rect.height
      setCreating({ x, y })
    },
    [selectedIssue]
  )

  const annotations = useMemo(
    () => issues.filter((i) => i.properties?.annotation),
    [issues]
  )

  return (
    <div>
      <div
        className="relative inline-block cursor-crosshair"
        onClick={handleImageClick}
        data-testid="annotation-image-container"
      >
        <img
          src={contentUrl}
          alt="Document"
          className="max-w-full"
          crossOrigin="use-credentials"
        />
        {/* Render pins */}
        {annotations.map((issue) => {
          const ann = issue.properties!.annotation!
          return (
            <AnnotationPin
              key={issue.uid}
              issue={issue}
              x={ann.x}
              y={ann.y}
              onClick={(e) => {
                e.stopPropagation()
                setSelectedIssue(selectedIssue?.uid === issue.uid ? null : issue)
              }}
            />
          )
        })}
        {/* Creating pin indicator */}
        {creating && (
          <div
            className="absolute -translate-x-1/2 -translate-y-full pointer-events-none"
            style={{ left: `${creating.x * 100}%`, top: `${creating.y * 100}%` }}
          >
            <AppIcon path={mdiMapMarker} size="lg" className="text-primary fill-primary/30" />
          </div>
        )}
      </div>

      {/* Create annotation popup */}
      {creating && (
        <AnnotationCreatePopup
          org={org}
          documentUid={documentUid}
          x={creating.x}
          y={creating.y}
          page={1}
          onClose={() => setCreating(null)}
          onCreated={() => {
            setCreating(null)
            onInvalidate()
          }}
        />
      )}

      {/* Edit annotation popup */}
      {selectedIssue && (
        <AnnotationEditPopup
          org={org}
          issue={selectedIssue}
          onClose={() => setSelectedIssue(null)}
          onUpdated={() => {
            setSelectedIssue(null)
            onInvalidate()
          }}
          onDeleted={() => {
            setSelectedIssue(null)
            onInvalidate()
          }}
        />
      )}
    </div>
  )
}

// --- PDF Annotation Viewer ---

function PdfAnnotationViewer({ org, contentUrl, documentUid, issues, onInvalidate }: ViewerProps) {
  const [numPages, setNumPages] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [creating, setCreating] = useState<{ x: number; y: number } | null>(null)
  const [selectedIssue, setSelectedIssue] = useState<IssueAnnotation | null>(null)

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages)
  }, [])

  const pageAnnotations = useMemo(
    () =>
      issues.filter(
        (i) => i.properties?.annotation && (i.properties.annotation.page || 1) === currentPage
      ),
    [issues, currentPage]
  )

  const handlePageClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (selectedIssue) {
        setSelectedIssue(null)
        return
      }
      const rect = e.currentTarget.getBoundingClientRect()
      const x = (e.clientX - rect.left) / rect.width
      const y = (e.clientY - rect.top) / rect.height
      setCreating({ x, y })
    },
    [selectedIssue]
  )

  return (
    <div>
      {/* Page navigation */}
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            data-testid="annotation-pdf-prev"
          >
            <AppIcon path={mdiChevronLeft} size="sm" tone="muted" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {numPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= numPages}
            onClick={() => setCurrentPage((p) => p + 1)}
            data-testid="annotation-pdf-next"
          >
            <AppIcon path={mdiChevronRight} size="sm" tone="muted" />
          </Button>
        </div>
      )}

      <Document
        file={contentUrl}
        suspense={false}
        onLoadSuccess={onDocumentLoadSuccess}
        loading={
          <div className="flex items-center gap-2 text-muted-foreground">
            <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin" />
            Loading PDF...
          </div>
        }
      >
        <div
          className="relative inline-block cursor-crosshair"
          onClick={handlePageClick}
          data-testid="annotation-pdf-container"
        >
          <Page pageNumber={currentPage} width={800} />
          {/* Render pins for current page */}
          {pageAnnotations.map((issue) => {
            const ann = issue.properties!.annotation!
            return (
              <AnnotationPin
                key={issue.uid}
                issue={issue}
                x={ann.x}
                y={ann.y}
                onClick={(e) => {
                  e.stopPropagation()
                  setSelectedIssue(selectedIssue?.uid === issue.uid ? null : issue)
                }}
              />
            )
          })}
          {creating && (
            <div
              className="absolute -translate-x-1/2 -translate-y-full pointer-events-none"
              style={{ left: `${creating.x * 100}%`, top: `${creating.y * 100}%` }}
            >
              <AppIcon path={mdiMapMarker} size="lg" className="text-primary fill-primary/30" />
            </div>
          )}
        </div>
      </Document>

      {creating && (
        <AnnotationCreatePopup
          org={org}
          documentUid={documentUid}
          x={creating.x}
          y={creating.y}
          page={currentPage}
          onClose={() => setCreating(null)}
          onCreated={() => {
            setCreating(null)
            onInvalidate()
          }}
        />
      )}

      {selectedIssue && (
        <AnnotationEditPopup
          org={org}
          issue={selectedIssue}
          onClose={() => setSelectedIssue(null)}
          onUpdated={() => {
            setSelectedIssue(null)
            onInvalidate()
          }}
          onDeleted={() => {
            setSelectedIssue(null)
            onInvalidate()
          }}
        />
      )}
    </div>
  )
}

// --- Shared Components ---

function AnnotationPin({
  issue,
  x,
  y,
  onClick,
}: {
  issue: IssueAnnotation
  x: number
  y: number
  onClick: (e: React.MouseEvent) => void
}) {
  const priorityConfig = PRIORITY_CONFIG[issue.priority] || PRIORITY_CONFIG[3]
  const isClosed = issue.status === 'closed'

  return (
    <button
      className={`absolute -translate-x-1/2 -translate-y-full cursor-pointer group ${
        isClosed ? 'opacity-50' : ''
      }`}
      style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
      onClick={onClick}
      title={issue.title}
      data-testid={`annotation-pin-${issue.uid}`}
    >
      <AppIcon path={mdiMapMarker} size="lg" className={`drop-shadow-md ${ priorityConfig.color.replace('bg-', 'text-') } fill-current`} />
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block">
        <div className="bg-popover text-popover-foreground text-xs rounded px-2 py-1 shadow whitespace-nowrap">
          {issue.title}
        </div>
      </div>
    </button>
  )
}

// --- Create Annotation Popup ---

function AnnotationCreatePopup({
  org,
  documentUid,
  x,
  y,
  page,
  onClose,
  onCreated,
}: {
  org: string
  documentUid: string
  x: number
  y: number
  page: number
  onClose: () => void
  onCreated: () => void
}) {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState('3')

  const createMutation = $api.useMutation(
    'post',
    '/api/v1/organizations/{orgCode}/issues'
  )

  const handleSave = useCallback(async () => {
    if (!title.trim()) return
    await createMutation.mutateAsync({
      params: { path: { orgCode: org } },
      body: {
        title: title.trim(),
        description: description.trim() || undefined,
        type: 'general',
        target_type: 'document',
        target_entity_uid: documentUid,
        priority: parseInt(priority),
        properties: {
          annotation: { type: 'point', x, y, page },
        },
      } as any,
    })
    onCreated()
  }, [title, description, priority, org, documentUid, x, y, page, createMutation, onCreated])

  return (
    <Card className="mt-4 max-w-md">
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-sm">New Annotation</h3>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="annotation-create-cancel">
            <AppIcon path={mdiClose} size="sm" />
          </Button>
        </div>
        <Input
          placeholder="Title (required)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave()
            if (e.key === 'Escape') onClose()
          }}
          data-testid="annotation-create-title"
        />
        <Textarea
          placeholder="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          data-testid="annotation-create-description"
        />
        <Select value={priority} onValueChange={setPriority}>
          <SelectTrigger data-testid="annotation-create-priority">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PRIORITY_CONFIG).map(([value, config]) => (
              <SelectItem key={value} value={value}>
                <div className="flex items-center gap-2">
                  <div className={`h-2 w-2 rounded-full ${config.color}`} />
                  {config.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          onClick={handleSave}
          disabled={!title.trim() || createMutation.isPending}
          className="w-full"
          data-testid="annotation-create-save"
        >
          {createMutation.isPending ? (
            <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin mr-2" />
          ) : (
            <AppIcon path={mdiCheck} size="sm" className="mr-2" />
          )}
          Save Annotation
        </Button>
      </CardContent>
    </Card>
  )
}

// --- Edit Annotation Popup ---

function AnnotationEditPopup({
  org,
  issue,
  onClose,
  onUpdated,
  onDeleted,
}: {
  org: string
  issue: IssueAnnotation
  onClose: () => void
  onUpdated: () => void
  onDeleted: () => void
}) {
  const [title, setTitle] = useState(issue.title)
  const [description, setDescription] = useState(issue.description || '')
  const [priority, setPriority] = useState(String(issue.priority))
  const [status, setStatus] = useState<'draft' | 'open' | 'in_progress' | 'closed'>(issue.status as any)
  const [deleteOpen, setDeleteOpen] = useState(false)

  useEffect(() => {
    setTitle(issue.title)
    setDescription(issue.description || '')
    setPriority(String(issue.priority))
    setStatus(issue.status as typeof status)
  }, [issue])

  const updateMutation = $api.useMutation(
    'patch',
    '/api/v1/organizations/{orgCode}/issues/{issueUid}'
  )

  const deleteMutation = $api.useMutation(
    'delete',
    '/api/v1/organizations/{orgCode}/issues/{issueUid}'
  )

  const handleUpdate = useCallback(async () => {
    await updateMutation.mutateAsync({
      params: { path: { orgCode: org, issueUid: issue.uid } },
      body: {
        title,
        description,
        priority: parseInt(priority),
        status,
      },
    })
    onUpdated()
  }, [title, description, priority, status, org, issue.uid, updateMutation, onUpdated])

  const handleDelete = useCallback(async () => {
    await deleteMutation.mutateAsync({
      params: { path: { orgCode: org, issueUid: issue.uid } },
    })
    onDeleted()
  }, [org, issue.uid, deleteMutation, onDeleted])

  const priorityConfig = PRIORITY_CONFIG[issue.priority] || PRIORITY_CONFIG[3]

  return (
    <>
      <Card className="mt-4 max-w-md">
        <CardContent className="pt-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={priorityConfig.color.replace('bg-', 'border-')}>
                {priorityConfig.label}
              </Badge>
              <Badge variant="outline">{issue.status}</Badge>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="annotation-edit-close">
              <AppIcon path={mdiClose} size="sm" />
            </Button>
          </div>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleUpdate()
              if (e.key === 'Escape') onClose()
            }}
            data-testid="annotation-edit-title"
          />
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Description"
            data-testid="annotation-edit-description"
          />
          <div className="flex gap-2">
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="annotation-edit-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PRIORITY_CONFIG).map(([value, config]) => (
                  <SelectItem key={value} value={value}>
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-2 rounded-full ${config.color}`} />
                      {config.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
              <SelectTrigger data-testid="annotation-edit-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleUpdate}
              disabled={!title.trim() || updateMutation.isPending}
              className="flex-1"
              data-testid="annotation-edit-save"
            >
              {updateMutation.isPending ? (
                <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin mr-2" />
              ) : (
                <AppIcon path={mdiCheck} size="sm" className="mr-2" />
              )}
              Update
            </Button>
            <Button
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
              data-testid="annotation-edit-delete"
            >
              <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Annotation</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the annotation "{issue.title}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="annotation-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="annotation-delete-confirm"
            >
              {deleteMutation.isPending ? (
                <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin mr-2" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
